import { Package } from 'lucide-react';
import React, { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { ConfigurationCriteriaProductsRecapHeader } from 'src/features/configuration/configurationCriteria/configurationCriteriaProducts/ConfigurationCriteriaProductsRecapHeader';
import { ConfigurationCriteriaProductsRecapList } from 'src/features/configuration/configurationCriteria/configurationCriteriaProducts/ConfigurationCriteriaProductsRecapList';

type ConfigurationCriteriaProductsRecapProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  validSkus: string[];
  invalidSkus: string[];
  onSubmit: () => void;
  onCancel: () => void;
};

export function ConfigurationCriteriaProductsRecap({
  open,
  onOpenChange,
  validSkus,
  invalidSkus,
  onSubmit,
  onCancel,
}: ConfigurationCriteriaProductsRecapProps): ReactElement {
  const { t } = useTranslation('configuration');
  const totalSkus = useMemo(
    () => validSkus.length + invalidSkus.length,
    [validSkus, invalidSkus],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[80vh] max-w-2xl' closable={false}>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Package className='h-5 w-5' />
            {t('Import recap')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'Check the products that will be imported and those that will be ignored',
            )}
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-6'>
          <ConfigurationCriteriaProductsRecapHeader
            totalSkus={totalSkus}
            validSkus={validSkus.length}
            invalidSkus={invalidSkus.length}
          />
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
            <ConfigurationCriteriaProductsRecapList
              title={t('Valid products')}
              titleClassName='text-success-default'
              description={t('The following products will be imported')}
              skus={validSkus}
              listClassName='border-success-border bg-success-background'
            />
            <ConfigurationCriteriaProductsRecapList
              title={t('Invalid products')}
              titleClassName='text-error-default'
              description={t('The following products will be ignored')}
              skus={invalidSkus}
              listClassName='border-error-border bg-error-background'
            />
          </div>
        </div>
        <DialogFooter className='gap-2'>
          <Button variant='outline' onClick={onCancel}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={onSubmit}
            disabled={validSkus.length === 0}
            className='min-w-[120px]'
          >
            {validSkus.length === 0
              ? t('No valid products')
              : `${t('Import')} ${validSkus.length} ${t('product')}${validSkus.length > 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
